import { PointerEvent as ReactPointerEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

type ResizablePanelsProps = {
    left: ReactNode;
    right: ReactNode;
    initialLeftWidth?: number;
    minLeftWidth?: number;
    maxLeftWidth?: number;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export default function ResizablePanels({
    left,
    right,
    initialLeftWidth = 42,
    minLeftWidth = 24,
    maxLeftWidth = 76,
}: ResizablePanelsProps) {
    const isMobile = useMediaQuery("(max-width: 900px)");
    const containerRef = useRef<HTMLDivElement | null>(null);
    const frameRef = useRef<number | null>(null);
    const [leftWidth, setLeftWidth] = useState(initialLeftWidth);
    const [isDragging, setIsDragging] = useState(false);

    const stopDragging = () => {
        setIsDragging(false);
    };

    const updateWidthFromClientX = (clientX: number) => {
        if (!containerRef.current) {
            return;
        }

        if (frameRef.current !== null) {
            cancelAnimationFrame(frameRef.current);
        }

        frameRef.current = window.requestAnimationFrame(() => {
            if (!containerRef.current) {
                return;
            }

            const rect = containerRef.current.getBoundingClientRect();
            const nextWidth = ((clientX - rect.left) / rect.width) * 100;
            setLeftWidth(clamp(nextWidth, minLeftWidth, maxLeftWidth));
            frameRef.current = null;
        });
    };

    useEffect(() => {
        if (!isDragging) {
            document.body.style.userSelect = "";
            document.body.style.cursor = "";
            return undefined;
        }

        document.body.style.userSelect = "none";
        document.body.style.cursor = "col-resize";

        const handleWindowBlur = () => stopDragging();
        window.addEventListener("blur", handleWindowBlur);

        return () => {
            window.removeEventListener("blur", handleWindowBlur);
            document.body.style.userSelect = "";
            document.body.style.cursor = "";
            if (frameRef.current !== null) {
                cancelAnimationFrame(frameRef.current);
                frameRef.current = null;
            }
        };
    }, [isDragging]);

    const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        setIsDragging(true);
    };

    const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
        if (!isDragging) {
            return;
        }

        updateWidthFromClientX(event.clientX);
    };

    const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        stopDragging();
    };

    const containerStyle = useMemo(
        () => (isMobile
            ? {
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr)",
                gridTemplateRows: "minmax(260px, 42vh) minmax(0, 1fr)",
                gap: "14px",
                minHeight: 0,
                height: "100%",
            }
            : {
                display: "flex",
                minHeight: 0,
                height: "100%",
                gap: "14px",
            }),
        [isMobile],
    );

    return (
        <Box ref={containerRef} style={containerStyle}>
            <Box
                style={{
                    minWidth: 0,
                    minHeight: 0,
                    flex: isMobile ? undefined : `0 0 ${leftWidth}%`,
                }}
            >
                {left}
            </Box>

            {!isMobile && (
                <Box
                    role="separator"
                    aria-orientation="vertical"
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={stopDragging}
                    onLostPointerCapture={stopDragging}
                    style={{
                        width: "14px",
                        minWidth: "14px",
                        borderRadius: "999px",
                        background: "linear-gradient(180deg, #f0e6d9 0%, #ddcbb7 100%)",
                        border: "1px solid rgba(138, 106, 74, 0.26)",
                        cursor: "col-resize",
                        position: "relative",
                        boxShadow: isDragging ? "0 0 0 3px rgba(138, 106, 74, 0.16)" : "none",
                    }}
                >
                    <Box
                        style={{
                            position: "absolute",
                            inset: "50% auto auto 50%",
                            transform: "translate(-50%, -50%)",
                            width: "4px",
                            height: "54px",
                            borderRadius: "999px",
                            background: "repeating-linear-gradient(180deg, #8a6a4a 0 5px, transparent 5px 10px)",
                            opacity: 0.82,
                        }}
                    />
                </Box>
            )}

            <Box style={{ minWidth: 0, minHeight: 0, flex: 1 }}>
                {right}
            </Box>
        </Box>
    );
}

